import React, { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import metadata from './AddDriverPopup.metadata.json5';
import addDriverMessages from './AddDriverPopup.messages';
import editDriverMessages from './EditDriverPopup.messages';
import addDriverStyles from './AddDriverPopup.module.scss';
import { platformMessages as commonMessages } from 'ml-components-platform-react';
import {
    MLInsuredCandidatesComponent,
    MLNameDetailsComponent,
} from 'ml-components-common-react';

function AddDriverPopup(props) {
    const {
        checkIsDriverPNI, getFilteredGender, checkUniqueSelf, checkUniqueSpouse, driverList, viewModelService, saveAction, cancelAction, isEdit, submissionVM, getRatedDriverType, isQnB, driverVM, onCancel, onSave
    } = props;
    const translator = useTranslator();
    const [insuredCandidateSelectedML, setInsuredCandidateSelectedML] = useState(false)
    const { onValidate, isComponentValid } = useValidation('AddriverPopup');
    const { refreshData } = useDataRefresh();
    const driverVMLocal = useRef(_.cloneDeep(driverVM));
    const QUOTED_SUBMISSIONS_KEY = 'quotedSubmissionNumbers';

    useEffect(() => {
        if (driverVM) {
            driverVMLocal.current = _.cloneDeep(driverVM)
        }
    }, [driverVM])

    const vm = driverVM?.current || driverVM

    const insuredCandidatesProps = isEdit ? { visible: false } : {
        submissionVM,
        driverVM: vm,
        driverType: getRatedDriverType(),
        setInsuredCandidateSelected: setInsuredCandidateSelectedML,
        viewModelService
    };

    const nbGenderState = _.get(submissionVM, 'baseData.isValidNonBinaryGenderState_Ext.value');
    const modalTitle = `${translator(isEdit ? editDriverMessages.editDriverPopupTitle_Ext : addDriverMessages.AddDriverPopupTitle)}`;
    const isQnbEdit = isQnB && isEdit
    const dobYear = vm?.dateOfBirth?.year?.value || ''

    const isQuoted = (quoteNumber) => {
        if (!quoteNumber) return false;
        const list = JSON.parse(localStorage.getItem(QUOTED_SUBMISSIONS_KEY) || '[]');
        return list.includes(quoteNumber);
    };

    const onCancelHandler = () => {
        driverVMLocal.current = _.cloneDeep(driverVM)
        cancelAction()
        onCancel()
    }

    const onSaveHandler = () => {
        if(isComponentValid){
            if(saveAction.length > 0) {
                saveAction(driverVMLocal.current)
            } else {
                saveAction()
            }
            onSave()
        } else {
            onValidate(isComponentValid, 'AddriverPopup')
        }
    }

    const resolvers = {
        resolveCallbackMap: {
            onSaveHandler,
            onCancelHandler,
        },
        resolveComponentMap: {
            mlInsuredCandidatesComponent: MLInsuredCandidatesComponent,
            mlnamedetailscomponent: MLNameDetailsComponent
        },
        resolveClassNameMap: {
            modalBodyStyles: addDriverStyles
        }
    };

    const writeValue = (value, path) => {
        _.set(driverVMLocal.current, path, value);
        refreshData()
    };

    const onNameChange = (state) => {
        const { firstName, lastName } = state
        _.set(driverVMLocal.current, 'firstName', firstName)
        _.set(driverVMLocal.current, 'lastName', lastName)
        refreshData()
    }

    const overrides = {
        title: modalTitle,
        viewModel: vm,
        actionBtnLabel: isEdit ? commonMessages.mlSave : commonMessages.mlContinue,
        cancelBtnLabel: isEdit ? editDriverMessages.cancelLabel_Ext : undefined,
        insuredCandidatesComponent: insuredCandidatesProps,
        modalBodyStyles: addDriverStyles,
        discardChangesOnCancel: isEdit,
        formFieldsContainer: {
            visible: (insuredCandidateSelectedML && !isEdit) || isEdit
        },
        nameDetailsComponent: {
            personVM: _.get(vm, 'person'),
            isComponent: true,
            submissionVM,
            onModelChange: onNameChange

        },
        relationship: {
            disabled: isQnB && (submissionVM.value.isQuoted() || isQuoted(submissionVM?.quoteID?.value) || !_.isEmpty(_.get(submissionVM, 'quoteData.paymentQuotes_ML.value'))) && checkIsDriverPNI(driverVMLocal)
        },
        dob: {
            disabled: isQnB && (submissionVM.value.isQuoted() || isQuoted(submissionVM?.quoteID?.value) || !_.isEmpty(_.get(submissionVM, 'quoteData.paymentQuotes_ML.value'))) && checkIsDriverPNI(driverVMLocal),            
        },
        dobPopupControl: {
            className: isQnB ? "col-md-12 remove-form-group-spacing" : "col-md-4 remove-form-group-spacing"
        },
        dobRow: {
            className: isQnB ? "row help-text-row vertical-spacing-40 col-md-12 qnbFieldForToolTip" : "row help-text-row vertical-spacing-40 col-md-12"
        },
        statePopupControl: {
            className: isQnB ? "col-md-12 remove-form-group-spacing" : "col-md-4 remove-form-group-spacing"
        },
        dobTooltipContainer: {
            className: isQnB ? "col-md-6-qb tooltipContainerOverride" : "col-md-6 tooltipContainerOverride"
        },
        stateTooltipContainer: {
            className: isQnB ? "col-md-6-qb tooltipContainerOverride" : "col-md-6 tooltipContainerOverride"
        },
        checkUniqueSelf: {
            visible: driverList && !checkUniqueSelf(isEdit ? driverList : [vm.value, ...driverList])
        },
        checkUniqueSpouse: {
            visible: driverList && !checkUniqueSpouse(isEdit ? driverList : [vm.value, ...driverList])
        },
        genderRow: {
            availableValues: getFilteredGender(nbGenderState),
            disabled: isQnB && (submissionVM.value.isQuoted() || isQuoted(submissionVM?.quoteID?.value) || !_.isEmpty(_.get(submissionVM, 'quoteData.paymentQuotes_ML.value'))) && checkIsDriverPNI(driverVMLocal),
            className: isQnB ? "radioButtonQnB row col-md-4 vertical-spacing-10" : "row col-md-4 vertical-spacing-10"
        },
        saveButton: {
            className: isQnB ? "save-button-style" : ""
        },
        buttonsCol: {
            className: isQnB ? "col-md-12 button-col" : "col-md-12"
        }
    };

    const uiProps = useMemo(() => {
        const updatedData = _.cloneDeep(metadata.pageContent)
        const buttonsRow = {
            id: 'buttonsRow',
            type: 'container',
            component: 'div',
            componentProps: {
                className: 'row clearfix',
            },
            content: [
                {
                    id: 'buttonsCol',
                    type: 'container',
                    component: 'div',
                    componentProps: {
                        className: 'col-md-12',
                    },
                    content: [
                        {
                            id: 'saveButton',
                            type: 'action',
                            component: 'button',
                            componentProps: {
                                type: 'filled',
                                onClick: 'onSaveHandler',
                            },
                            content: isEdit ? {
                                id: 'ml.addriver-dialog.Save',
                                defaultMessage: 'Save',
                            } : {
                                id: 'ml.addriver-dialog.Continue',
                                defaultMessage: 'Continue',
                            },
                        },
                        {
                            id: 'closeButton',
                            type: 'action',
                            component: 'button',
                            componentProps: {
                                type: 'outlined',
                                onClick: 'onCancelHandler', 
                            },
                            content: {
                                id: 'ml.addriver-dialog.Cancel',
                                defaultMessage: 'Cancel',
                            },
                        },
                    ],
                },
            ]
        }
        const existingData = _.get(updatedData, 'content', [])
        _.set(updatedData, 'content', [
            ...existingData, buttonsRow
        ])
        return updatedData
    }, [metadata.pageContent, isEdit])

    return (
        <ViewModelForm
            uiProps={uiProps}
            model={vm}
            onValueChange={writeValue}
            onValidationChange={onValidate}
            overrideProps={overrides}
            showErrors
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

AddDriverPopup.propTypes = {
    model: PropTypes.shape({}).isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};
export default AddDriverPopup;
